import { MEMBER_ROLES, memberRole } from '@kanvas/board-core';
import { useMemo, useState } from 'react';
import type { Organization } from '../lib/api';
import type { RosterMember } from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDateShort } from './dates';

export interface PeopleViewProps {
  org: Organization;
  /**
   * Everyone the viewer may see: the whole roster for the owner, and just
   * their own students for a teacher.
   */
  roster: RosterMember[];
  loading: boolean;
  /** The owner edits roles, links and payments; a teacher only looks. */
  canManage: boolean;
  onOpenPerson: (userId: string) => void;
  onGiveTask: (studentId: string) => void;
}

type Tab = 'all' | 'students' | 'teachers' | 'curators';

/**
 * Organisation directory: everyone who joined, then filtered by tutoring role.
 *
 * Invites land people with no kind yet — they are already members, but not
 * teachers or students. The «Все» tab is the default so a new invitee is
 * visible the moment they accept, rather than waiting for someone to assign
 * a role first.
 */
export function PeopleView({
  org,
  roster,
  loading,
  canManage,
  onOpenPerson,
  onGiveTask,
}: PeopleViewProps) {
  const [tab, setTab] = useState<Tab>(canManage ? 'all' : 'students');
  const [query, setQuery] = useState('');

  const students = useMemo(() => roster.filter((m) => m.kind === 'student'), [roster]);
  const teachers = useMemo(() => roster.filter((m) => m.kind === 'teacher'), [roster]);
  const curators = useMemo(() => roster.filter((m) => m.kind === 'curator'), [roster]);

  const nameOf = (userId: string) => roster.find((m) => m.userId === userId)?.name ?? null;

  const pools: Record<Tab, RosterMember[]> = {
    all: roster,
    students,
    teachers,
    curators,
  };
  const pool = pools[tab];

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = !needle
      ? pool
      : pool.filter(
          (person) =>
            person.name.toLowerCase().includes(needle) ||
            person.email.toLowerCase().includes(needle),
        );

    // Owners first, then the roles in order of standing, and people still
    // without one last — so a fresh invitee is not buried under a long
    // student list.
    const rank = (m: RosterMember) => {
      if (m.role === 'admin') return 0;
      const at = MEMBER_ROLES.findIndex((role) => role.slug === m.kind);
      return at === -1 ? MEMBER_ROLES.length + 1 : at + 1;
    };
    return [...filtered].sort(
      (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'ru'),
    );
  }, [pool, query]);

  const subtitle = (() => {
    if (loading) return 'Загрузка…';
    if (!canManage) return `${org.name} · ${students.length} закреплено за вами`;
    const parts = [
      `${roster.length} участников`,
      `${students.length} учеников`,
      `${teachers.length} учителей`,
    ];
    // Only mentioned once the organisation actually uses curators, so nothing
    // changes for the ones that do not.
    if (curators.length > 0) parts.push(`${curators.length} кураторов`);
    return `${org.name} · ${parts.join(' · ')}`;
  })();

  const emptyCopy: Record<Tab, { icon: 'users' | 'graduation' | 'shield'; title: string; body: string }> = {
    all: {
      icon: 'users',
      title: 'Участников пока нет',
      body: 'Пригласите людей ссылкой из левой панели — они появятся здесь сразу после регистрации.',
    },
    students: {
      icon: 'graduation',
      title: 'Учеников пока нет',
      body: canManage
        ? 'Откройте профиль участника и назначьте роль «Ученик».'
        : 'Владелец организации ещё не закрепил за вами учеников.',
    },
    teachers: {
      icon: 'users',
      title: 'Учителей пока нет',
      body: 'Откройте профиль участника и назначьте роль «Учитель».',
    },
    curators: {
      icon: 'shield',
      title: 'Кураторов пока нет',
      body: 'Откройте профиль участника и назначьте роль «Куратор» — она видна в звонках и чате.',
    },
  };

  const renderRow = (person: RosterMember) => {
    const role = memberRole(person.kind);
    const isStudent = person.kind === 'student';
    const isTeacher = person.kind === 'teacher';
    const linked = (isStudent ? person.teacherIds : isTeacher ? person.studentIds : [])
      .map(nameOf)
      .filter((name): name is string => name !== null);

    return (
      <li key={person.userId} className="person-row">
        <button type="button" className="person-main" onClick={() => onOpenPerson(person.userId)}>
          <span className="person-avatar" style={{ background: person.color }}>
            {person.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="person-text">
            <span className="person-name">
              {person.name}
              {person.role === 'admin' && <span className="kind-badge is-owner">Владелец</span>}
              {tab === 'all' && role && (
                <span className={`kind-badge is-${role.slug}`}>{role.label}</span>
              )}
              {tab === 'all' && !person.kind && person.role !== 'admin' && (
                <span className="kind-badge is-none">Без роли</span>
              )}
            </span>
            <span className="person-sub">
              {isStudent ? (
                linked.length > 0 ? (
                  <>
                    <Icon name="graduation" size={12} />
                    {linked.join(', ')}
                  </>
                ) : (
                  <em className="person-dim">без учителя</em>
                )
              ) : isTeacher ? (
                linked.length > 0 ? (
                  <>
                    <Icon name="users" size={12} />
                    {linked.join(', ')}
                  </>
                ) : (
                  <em className="person-dim">учеников нет</em>
                )
              ) : (
                person.email
              )}
            </span>
          </span>

          {isStudent && canManage && (
            <span className={`pay-chip ${person.paid ? 'is-paid' : ''}`}>
              <Icon name="wallet" size={12} />
              {person.paid
                ? person.paidAt
                  ? `оплачено ${formatDateShort(new Date(person.paidAt).toISOString())}`
                  : 'оплачено'
                : 'не оплачено'}
            </span>
          )}
        </button>

        <span className="person-actions">
          {isStudent && (
            <button
              type="button"
              aria-label={`Дать задание: ${person.name}`}
              data-tip="Дать задание"
              onClick={() => onGiveTask(person.userId)}
            >
              <Icon name="plus" size={14} />
            </button>
          )}
          <button
            type="button"
            aria-label={`Открыть профиль: ${person.name}`}
            data-tip="Профиль"
            onClick={() => onOpenPerson(person.userId)}
          >
            <Icon name="chevronDown" size={14} className="rotate-270" />
          </button>
        </span>
      </li>
    );
  };

  const empty = emptyCopy[tab];

  return (
    <section className="dash-content">
      <header className="dash-header">
        <div>
          <h1>Участники</h1>
          <p className="dash-subtitle">{subtitle}</p>
        </div>
        {roster.length > 4 && (
          <label className="people-search">
            <Icon name="search" size={14} />
            <input
              type="search"
              placeholder="Найти человека"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        )}
      </header>

      {/* A teacher has only their linked students, so the tabs would be a
          control with one option — they skip straight to that list. */}
      {canManage && (
        <div className="people-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'all'}
            className={tab === 'all' ? 'is-active' : ''}
            onClick={() => setTab('all')}
          >
            <Icon name="users" size={15} />
            Все
            <em>{roster.length}</em>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'students'}
            className={tab === 'students' ? 'is-active' : ''}
            onClick={() => setTab('students')}
          >
            <Icon name="graduation" size={15} />
            Ученики
            <em>{students.length}</em>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'teachers'}
            className={tab === 'teachers' ? 'is-active' : ''}
            onClick={() => setTab('teachers')}
          >
            <Icon name="users" size={15} />
            Учителя
            <em>{teachers.length}</em>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'curators'}
            className={tab === 'curators' ? 'is-active' : ''}
            onClick={() => setTab('curators')}
          >
            <Icon name="shield" size={15} />
            Кураторы
            <em>{curators.length}</em>
          </button>
        </div>
      )}

      {!loading && visible.length === 0 ? (
        <div className="dash-empty">
          <Icon name={empty.icon} size={28} />
          <h2>{empty.title}</h2>
          <p>{empty.body}</p>
        </div>
      ) : (
        <ul className="person-list">{visible.map(renderRow)}</ul>
      )}
    </section>
  );
}
